// src/features/wf3/popup/W3EditarRecriacaoModal.jsx
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  getCausadores,
  getCausasPrimarias,
  getCausasSecundarias,
} from "../api";
import s from "./W3FinalizarModal.module.css";

/* Utils (mesmos do W3FinalizarModal) */
const uniqSorted = (arr) =>
  Array.from(new Set((arr || []).filter(Boolean))).sort((a, b) =>
    String(a).localeCompare(String(b), "pt-BR")
  );

const CORRECOES = [
  [/\brpévia\b/gi, "prévia"],
  [/\bprevia\b/gi, "prévia"],
  [/\bprim[ée]ria\b/gi, "primeira"],
  [/\bsegubda\b/gi, "segunda"],
  [/\binicio\b/gi, "início"],
  [/\btermino\b/gi, "término"],
  [/\bobs\b/gi, "observação"],
];

const corrigePT = (txt = "") => {
  let out = String(txt || "");
  for (const [re, rep] of CORRECOES) out = out.replace(re, rep);
  return out.replace(/\s{2,}/g, " ").trim();
};

const foldSearch = (value = "") =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

function FieldDropdown({
  value,
  onChange,
  options = [],
  placeholder = "Selecione...",
  disabled = false,
  searchable = false,
  searchPlaceholder = "Digite para filtrar...",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [menuStyle, setMenuStyle] = useState(null);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const inputRef = useRef(null);

  const safeOptions = useMemo(
    () =>
      (Array.isArray(options) ? options : [])
        .map((opt) => String(opt ?? "").trim())
        .filter(Boolean),
    [options]
  );

  const filteredOptions = useMemo(() => {
    if (!searchable) return safeOptions;
    const foldedQuery = foldSearch(query);
    if (!foldedQuery) return safeOptions;
    return safeOptions.filter((opt) => foldSearch(opt).includes(foldedQuery));
  }, [query, safeOptions, searchable]);

  useEffect(() => {
    if (!searchable) {
      setQuery("");
      return;
    }
    if (!open) setQuery(value || "");
  }, [open, searchable, value]);

  useEffect(() => {
    if (!open || !searchable) return undefined;
    const timer = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [open, searchable]);

  useEffect(() => {
    if (!open) return undefined;

    const onOutside = (event) => {
      if (rootRef.current?.contains(event.target)) return;
      if (menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const onEsc = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onOutside);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return undefined;
    }

    const updatePosition = () => {
      const anchorEl = triggerRef.current || rootRef.current;
      if (!anchorEl) return;

      const rect = anchorEl.getBoundingClientRect();
      const boundary =
        anchorEl.closest(`.${s.content}`) || anchorEl.closest(`.${s.modal}`);
      const boundaryRect = boundary?.getBoundingClientRect?.() || null;
      const gap = 8;
      const itemCount = Math.max(filteredOptions.length, 1);
      const idealHeight = Math.min(Math.max(itemCount * 40, searchable ? 144 : 96), 220);
      const spaceBelow = boundaryRect
        ? boundaryRect.bottom - rect.bottom
        : window.innerHeight - rect.bottom;
      const spaceAbove = boundaryRect ? rect.top - boundaryRect.top : rect.top;
      const openUp = spaceBelow < idealHeight && spaceAbove > spaceBelow;
      const availableSpace = Math.floor((openUp ? spaceAbove : spaceBelow) - gap);
      const available = Math.max(48, Math.min(idealHeight, availableSpace));
      const menuNaturalHeight = Math.min(
        menuRef.current?.getBoundingClientRect?.().height ||
          menuRef.current?.scrollHeight ||
          idealHeight,
        available
      );
      const anchoredTop = openUp
        ? rect.top - gap - menuNaturalHeight
        : rect.bottom + gap;

      setMenuStyle({
        top: `${Math.round(anchoredTop)}px`,
        left: `${Math.round(rect.left)}px`,
        width: `${Math.round(rect.width)}px`,
        maxHeight: `${available}px`,
      });
    };

    updatePosition();
    const rafId = window.requestAnimationFrame(updatePosition);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [filteredOptions.length, open, searchable]);

  return (
    <div
      ref={rootRef}
      className={[s.select, open ? s.selectOpen : "", disabled ? s.selectDisabled : ""]
        .filter(Boolean)
        .join(" ")}
    >
      {searchable ? (
        open ? (
          <div
            ref={triggerRef}
            className={[s.input, s.selectTrigger, s.selectTriggerSearch, s.selectTriggerEditing]
              .filter(Boolean)
              .join(" ")}
            role="combobox"
            aria-haspopup="listbox"
            aria-expanded={open}
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                event.preventDefault();
                setOpen(false);
                inputRef.current?.blur();
              }
            }}
          >
            <input
              ref={inputRef}
              type="text"
              value={query}
              disabled={disabled}
              className={[s.selectInlineInput, value || query ? "" : s.selectPlaceholder]
                .filter(Boolean)
                .join(" ")}
              placeholder={searchPlaceholder || placeholder}
              onMouseDown={(event) => {
                if (document.activeElement === inputRef.current) {
                  event.preventDefault();
                  setOpen(false);
                  inputRef.current?.blur();
                }
              }}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
        ) : (
          <button
            ref={triggerRef}
            type="button"
            className={[s.input, s.selectTrigger].join(" ")}
            onClick={() => {
              if (disabled) return;
              setQuery(value || "");
              setOpen(true);
            }}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={open}
          >
            <span
              className={[s.selectValue, value ? "" : s.selectPlaceholder]
                .filter(Boolean)
                .join(" ")}
            >
              {value || placeholder}
            </span>
          </button>
        )
      ) : (
        <button
          ref={triggerRef}
          type="button"
          className={[s.input, s.selectTrigger].join(" ")}
          onClick={() => {
            if (!disabled) setOpen((prev) => !prev);
          }}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span
            className={[s.selectValue, value ? "" : s.selectPlaceholder]
              .filter(Boolean)
              .join(" ")}
          >
            {value || placeholder}
          </span>
        </button>
      )}

      {open && menuStyle
        ? createPortal(
            <div
              ref={menuRef}
              className={s.selectMenuFloating}
              role="listbox"
              style={menuStyle}
            >
              <div className={s.selectList}>
                {filteredOptions.length ? (
                  filteredOptions.map((opt) => {
                    const selected = opt === value;
                    return (
                      <button
                        key={opt}
                        type="button"
                        className={[s.selectOption, selected ? s.selectOptionSelected : ""]
                          .filter(Boolean)
                          .join(" ")}
                        onClick={() => {
                          onChange(opt);
                          setQuery(opt);
                          setOpen(false);
                        }}
                        aria-selected={selected}
                      >
                        {opt}
                      </button>
                    );
                  })
                ) : (
                  <div className={s.selectEmpty}>Nenhuma opcao encontrada</div>
                )}
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

/**
 * Modal usado quando atividade ou SB de trabalho foram alterados
 * e pode haver RECRIAÇÃO da programação.
 *
 * Ele coleta responsabilidade / impacto / causa / justificativa da alteração.
 */
export default function W3EditarRecriacaoModal({
  open = false,
  atividadeAlterada = false,
  sbTrabalhoAlterado = false,
  onConfirm = () => {},
  onCancel = () => {},
}) {
  const [form, setForm] = useState({
    responsabilidade: "",
    impacto: "",
    causa: "",
    justificativa: "",
  });

  const [causadores, setCausadores] = useState([]);
  const [primarias, setPrimarias] = useState([]);
  const [secundarias, setSecundarias] = useState([]);
  const [salvando, setSalvando] = useState(false);

  // lista de campos críticos alterados
  const camposCriticos = useMemo(() => {
    const list = [];
    if (atividadeAlterada) list.push("Atividade");
    if (sbTrabalhoAlterado) list.push("SB de Trabalho");
    return list;
  }, [atividadeAlterada, sbTrabalhoAlterado]);

  // reset do form sempre que abrir
  useEffect(() => {
    if (open) {
      setForm({
        responsabilidade: "",
        impacto: "",
        causa: "",
        justificativa: "",
      });
    }
  }, [open]);

  // Causadores (responsabilidade)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const list = await getCausadores();
        if (alive) setCausadores(uniqSorted(list));
      } catch {
        if (alive) setCausadores([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Causas primárias (impacto)
  useEffect(() => {
    let alive = true;

    if (!form.responsabilidade) {
      setPrimarias([]);
      setSecundarias([]);
      return;
    }

    (async () => {
      try {
        const list = await getCausasPrimarias(form.responsabilidade);
        if (alive) setPrimarias(uniqSorted(list));
      } catch {
        if (alive) setPrimarias([]);
      } finally {
        if (alive) setSecundarias([]);
      }
    })();

    return () => {
      alive = false;
    };
  }, [form.responsabilidade]);

  // Causas secundárias (causa)
  useEffect(() => {
    let alive = true;

    if (!form.responsabilidade || !form.impacto) {
      setSecundarias([]);
      return;
    }

    (async () => {
      try {
        const list = await getCausasSecundarias(
          form.responsabilidade,
          form.impacto
        );
        if (alive) setSecundarias(uniqSorted(list));
      } catch {
        if (alive) setSecundarias([]);
      }
    })();

    return () => {
      alive = false;
    };
  }, [form.responsabilidade, form.impacto]);

  // ESC fecha
  const onKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") onCancel();
    },
    [onCancel]
  );

  useEffect(() => {
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onKeyDown]);

  /* ===== Handlers ===== */

  const handleChangeForm = (e) => {
    const { name, value } = e.target;

    if (name === "responsabilidade") {
      setForm((f) => ({
        ...f,
        responsabilidade: value,
        impacto: "",
        causa: "",
      }));
      return;
    }

    if (name === "impacto") {
      setForm((f) => ({
        ...f,
        impacto: value,
        causa: "",
      }));
      return;
    }

    setForm((f) => ({
      ...f,
      [name]: value,
    }));
  };

  const setField = (name, value) => {
    if (name === "responsabilidade") {
      setForm((f) => ({
        ...f,
        responsabilidade: value,
        impacto: "",
        causa: "",
      }));
      return;
    }

    if (name === "impacto") {
      setForm((f) => ({
        ...f,
        impacto: value,
        causa: "",
      }));
      return;
    }

    setForm((f) => ({
      ...f,
      [name]: value,
    }));
  };

  const canConfirm = useMemo(() => {
    const { responsabilidade, impacto, causa, justificativa } = form;
    return (
      !!responsabilidade &&
      !!impacto &&
      !!causa &&
      String(justificativa).trim().length > 0
    );
  }, [form]);

  const submit = async () => {
    if (!canConfirm || salvando) return;

    setSalvando(true);
    try {
      const just = corrigePT(form.justificativa);

      await onConfirm({
        responsabilidade_alteracao: form.responsabilidade,
        impacto_alteracao: form.impacto,
        causa_alteracao: form.causa,
        justificativa_alteracao: just,
        motivo_alteracao: just,
      });
    } finally {
      setSalvando(false);
    }
  };

  /* ===== Render ===== */

  if (!open) return null;

  return (
    <div className={s.backdrop} onClick={onCancel}>
      <div
        className={s.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Alteração crítica da programação"
      >
        {/* Header */}
        <div className={s.header}>
          <div className={s.title}>Alterar campos críticos</div>
          <button
            onClick={onCancel}
            className={s.close}
            aria-label="Fechar"
            type="button"
          >
            ×
          </button>
        </div>

        {/* Conteúdo */}
        <div className={s.content}>
          <div className={s.card}>
            <div className={s.sectionTitle}>Recriação da programação</div>

            <p className={s.description}>
              Você alterou campos críticos desta programação. Ao salvar, o
              sistema poderá <strong>recriar</strong> a atividade (abrindo um
              novo fluxo no workflow e mantendo o histórico da atual).
            </p>

            {camposCriticos.length > 0 && (
              <>
                <p className={s.description}>Campos alterados:</p>
                <ul className={s.list}>
                  {camposCriticos.map((campo) => (
                    <li key={campo} className={s.listItem}>
                      {campo}
                    </li>
                  ))}
                </ul>
              </>
            )}

            <p className={s.description}>
              Informe abaixo a responsabilidade, o impacto, a causa e a
              justificativa dessa alteração:
            </p>

            <div className={s.grid3}>
              {/* RESPONSABILIDADE */}
              <div className={s.field}>
                <label className={s.label}>
                  RESPONSABILIDADE <span className={s.req}>*</span>
                </label>
                <FieldDropdown
                  value={form.responsabilidade}
                  onChange={(nextValue) => setField("responsabilidade", nextValue)}
                  options={causadores}
                  placeholder="Selecione a responsabilidade"
                  searchable
                  searchPlaceholder="Buscar responsabilidade..."
                />
              </div>

              {/* IMPACTO */}
              <div className={s.field}>
                <label className={s.label}>
                  IMPACTO <span className={s.req}>*</span>
                </label>
                <FieldDropdown
                  value={form.impacto}
                  onChange={(nextValue) => setField("impacto", nextValue)}
                  disabled={!form.responsabilidade}
                  options={primarias}
                  placeholder="Selecione o impacto"
                  searchable
                  searchPlaceholder="Buscar impacto..."
                />
              </div>

              {/* CAUSA */}
              <div className={s.field}>
                <label className={s.label}>
                  CAUSA <span className={s.req}>*</span>
                </label>
                <FieldDropdown
                  value={form.causa}
                  onChange={(nextValue) => setField("causa", nextValue)}
                  disabled={!form.responsabilidade || !form.impacto}
                  options={secundarias}
                  placeholder="Selecione a causa"
                  searchable
                  searchPlaceholder="Buscar causa..."
                />
              </div>
            </div>

            {/* JUSTIFICATIVA */}
            <div className={s.justificativaRow}>
              <label className={s.label}>
                JUSTIFICATIVA <span className={s.req}>*</span>
              </label>
              <textarea
                name="justificativa"
                className={`${s.input} ${s.textarea}`}
                rows={4}
                placeholder="Explique resumidamente o motivo da alteração/recriação"
                value={form.justificativa}
                onChange={handleChangeForm}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={s.actions}>
          <button onClick={onCancel} className={s.btn} type="button">
            Voltar
          </button>
          <button
            onClick={submit}
            className={`${s.btn} ${s.btnPrimary}`}
            disabled={!canConfirm || salvando}
            type="button"
          >
            {salvando ? "Enviando..." : "Salvar programação assim mesmo"}
          </button>
        </div>
      </div>
    </div>
  );
}
